import { cn } from "../lib/cn";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface AvatarProps {
  name: string;
  src?: string;
  size?: AvatarSize;
  /** Status ring colour for online / streak indicators. */
  status?: "online" | "rest" | "alert" | null;
  className?: string;
}

const sizes: Record<AvatarSize, string> = {
  xs: "h-6 w-6 text-[0.6rem]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
  xl: "h-20 w-20 text-2xl",
};

const statusRing = {
  online: "ring-2 ring-lime ring-offset-2 ring-offset-paper",
  rest: "ring-2 ring-ocean ring-offset-2 ring-offset-paper",
  alert: "ring-2 ring-coral ring-offset-2 ring-offset-paper",
};

// Deterministic Pulse gradient per name so initials avatars feel intentional.
const tints = [
  "bg-grad-coral text-white",
  "bg-grad-violet text-white",
  "bg-grad-ocean text-white",
  "bg-grad-lime text-white",
  "bg-grad-amber text-white",
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function hashIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

/** Circular avatar — image when available, deterministic initials otherwise. */
export function Avatar({ name, src, size = "md", status = null, className }: AvatarProps) {
  const tint = tints[hashIndex(name, tints.length)];
  return (
    <span
      className={cn(
        "inline-grid place-items-center overflow-hidden rounded-full font-display font-bold",
        sizes[size],
        !src && tint,
        status && statusRing[status],
        className,
      )}
      title={name}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden>{initials(name)}</span>
      )}
      {src && <span className="sr-only">{name}</span>}
    </span>
  );
}
